<script>
    import {PATH_PREFIX} from '../../../apiUrls.js';
    import {sgettext} from '../../../i18n.js';
    import {debounced, get, submit} from '../../../utils.js';

    import DataTable from '../../DataGrid/DataTable.svelte';
    import Errors from '../../Errors.svelte';
    import WorkSequenceOperationColumn from './WorkSequenceOperationColumn.svelte'
    import OperationWorkSequencesColumn from './OperationWorkSequencesColumn.svelte'
    import PartsSummaryColumn from './PartsSummaryColumn.svelte'

    export let activityVersionId = 0;
    export let taskListConfigurationId = 0;
    export let template = true;
    export let group = null;
    export let group_counter = null;
    export let operation_numbers = null;

    let listUrl = `${PATH_PREFIX}/sw/v2/api/work_sequence_task_list_operations/`;  // {% url 'internal-v2:work-sequence-task-list-operations-list' %}
    let optionsUrl = `${PATH_PREFIX}/sw/v2/api/activity_version_task_list_configuration_operations/`; // // {% url 'internal-v2:activity-version-task-list-configuration-operations-list' %}
    let operationOptions = [];
    let rows = [];

    let downloadSequencesOperationsSequencesUrl = `${PATH_PREFIX}/sw/v2/api/download_sequences_operations_excel/`;
    let downloadOperationsSequencesUrl = `${PATH_PREFIX}/sw/v2/api/download_operations_sequences_excel/`;

    $: unmappedSequences = false;

    const DEFAULT_COLUMNS = {
        name: {
            label: sgettext('$$SEQUENCE$$')
        },
        role_name: {
            label: sgettext('$$ROLE$$')
        },
        start_offset: {
            label: sgettext('$$START_TIME$$')
        },
        end_offset: {
            label: sgettext('$$END_TIME$$')
        },
        task_list_operation: {
            label: sgettext('$$SAP_OPERATION$$'),
            renderer: WorkSequenceOperationColumn,
            props: {
                // All props are dynamically determined by getColumns(), values below don't do anything
                options: [],
                template: template,
            }
        },
    };

    let columns = DEFAULT_COLUMNS;

    async function checkUnmappedSequences() {
        unmappedSequences = rows?.some((sequence) => {return sequence.task_list_operation === null;});
    }

    const handleOperationWorkSequencesSync = debounced(1000, () => {
        getOperations();
        checkUnmappedSequences();
    });

    async function getOperations() {
        if (activityVersionId) {
            const response = await get(`${optionsUrl}?role__activity_version=${activityVersionId}&configuration=${taskListConfigurationId}`);
            const json = await response.json();

            if (json) {
                operationOptions = json['results'];
            }
        }
    }

    async function getColumns() {
        await getOperations();

        columns = {
            ...DEFAULT_COLUMNS,
            task_list_operation: {
                label: sgettext('$$SAP_OPERATION$$'),
                renderer: WorkSequenceOperationColumn,
                props: {
                    options: operationOptions,
                    operationWorkSequencesUpdateHandler: handleOperationWorkSequencesSync,
                    template: template,
                }
            },
        }
    }

    async function getWorkSequences() {
        if (activityVersionId) {
            await getColumns();

            const response = await get(`${listUrl}?role__activity_version=${activityVersionId}&task_list_configuration=${taskListConfigurationId}`);
            const json = await response.json();

            if (json) {
                rows = json['results'];
            }
            checkUnmappedSequences();
        }
    }

    async function excelSequencesToOperationsClick(){
        const payload = {
            data: rows,
        };

        const url = `${downloadSequencesOperationsSequencesUrl}`;
        await submit(url, 'POST', payload).then(response => response.body.getReader().read()).then((file) => {
            let contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            let filename = "Sequences.xlsx"
            let blob = new Blob([file.value], { type: contentType })
            let downloadUrl = URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
        });
    }

    async function excelOperationsToSequencesClick(){
        const payload = {
            data: operationOptions,
        };

        const url = `${downloadOperationsSequencesUrl}`;
        await submit(url, 'POST', payload).then(response => response.body.getReader().read()).then((file) => {
            let contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            let filename = "Operations.xlsx"
            let blob = new Blob([file.value], { type: contentType })
            let downloadUrl = URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
        });
    }

    $: if (activityVersionId) {
        getWorkSequences();
    }

    let operationColumns = {
        operation: {
            label: sgettext('$$OPERATION$$'),
            sourceObjectName: 'operation',
            keyPath: 'number'
        },
        role_count: {
            label: sgettext("$$ROLE_COUNT$$"),
        },
        duration: {
            label: sgettext("$$DURATION$$"),
            tooltip: sgettext('$$DURATION_DURATION_ENTIRE_OPERATION_TIME$$')
        },
        minimum_duration: {
            label: sgettext("$$OPTIMUM_DURATION$$"),
            tooltip: sgettext('$$OPTIMUM_DURATION_ENTIRE_OPERATION_GAPS$$')
        },
        parts: {
            label: sgettext('$$PARTS$$'),
            renderer: PartsSummaryColumn
        },
        description: {
            label: sgettext('$$DESCRIPTION$$'),
            sourceObjectName: 'operation',
            keyPath: 'description'
        },
        work_sequences: {
            label: sgettext('$$WORK_SEQUENCES$$'),
            renderer: OperationWorkSequencesColumn
        }
    }

</script>

<div class="wrapper">
  <h4>
      {sgettext('$$GROUP_NUMBER$$')}: {group}, {sgettext('$$COUNTER$$')}: {group_counter} <small>{sgettext('$$OPERATIONS$$')}: {operation_numbers}</small>
  </h4>
  <p>
    {sgettext('$$MAP_OPERATION_NUMBER_SEQUENCE_ACTIVITY$$')}
  </p>

  {#if unmappedSequences}
    <Errors errors={sgettext("$$ACTIVITY_TASK_LISTS_SEQUENCES_NOT_MAPPED$$")}/>
  {/if}

  <div class="mb-2 d-flex justify-content-between">
    <button class="btn btn-outline" on:click={() => excelSequencesToOperationsClick()}>
        <i class="fa fa-file-excel-o"></i>
        {sgettext('$$EXPORT_EXCEL$$')}
    </button>
    <div>
        <i class="fas fa-square fa-fw text-warning"></i> {sgettext("$$USERDEFINED$$")}
        <i class="fas fa-square fa-fw text-danger"></i> {sgettext("$$UNMAPPED$$")}
    </div>
  </div>

  <DataTable {columns} {rows} />

  <br />

  <h4>{sgettext('$$OPERATIONS_REPORT$$')}</h4>
  <p>
    {sgettext('$$RESULTING_SAP_OPERATION_MAPPING_ACTIVITY$$')}:
  </p>
  <div class="mb-2">
    <button class="btn btn-outline" on:click={() => excelOperationsToSequencesClick()}>
      <i class="fa fa-file-excel-o"></i>
      {sgettext('$$EXPORT_EXCEL$$')}
    </button>
  </div>
  <DataTable columns={operationColumns} rows={operationOptions} />
</div>
